import Cookie from 'js-cookie'
import _ from 'lodash'
import { ANNUAL_PLANS, BUSINESS_PLUS_PLANS } from '@/constants'

const state = () => ({
  authenticated: false,
  user: null
})

const getters = {
  authenticated (state) {
    return state.authenticated
  },
  user (state) {
    return state.user
  },
  userRole (state) {
    return state.user.roles.map(({name}) => name)
  },
  isSuperAdmin (state) {
    return !!state.user.roles.find(role => role.name === 'superadministrator')
  },
  isSupport (state) {
    return !!state.user.roles.find(role => role.name === 'support')
  },
  userSubscription (state) {
    return state.user?.user_subscription
  },
  isLifetime (state) {
    return ['FP_PLATINUM', 'FP_DIAMOND', 'FP_GOLD', 'BUSINESS_LIFETIME'].includes(state.user?.user_subscription?.plan?.code) && state.user?.user_subscription.paid_amount === '0.00'
      || state.user?.user_subscription?.plan?.code === 'BUSINESS_PLUS_F_Y' && state.user?.user_subscription?.payment_method === 'FP'
  },
  expDateAt (state) {
    const raw = state.user?.exp_date?.date
    if (!raw) return null

    const s = String(raw).trim()

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(`${s}T23:59:59`)
      return Number.isNaN(d.getTime()) ? null : d
    }

    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
  },

  expDateExpired (state, getters) {
    const d = getters.expDateAt
    return !!d && d.getTime() < Date.now()
  },

  pendingReactivation (state, getters) {
    return !!state.user?.exp_date?.days_overdue && !getters.expDateExpired
  },
  subscriptionIsActive (state) {
    return state.user?.user_subscription?.status && ['succeeded', 'succeeeded'].includes(state.user?.user_subscription?.status)
  },
  canCreateProject (state) {
    return state.user.user_subscription?.plan?.projects_count >= state.user.user_subscription.projects_count
  },
  isBusinessPlus (state) {
    return _.includes(BUSINESS_PLUS_PLANS, state.user.user_subscription?.plan?.code) || (state.user.user_subscription?.plan?.is_business_plus || state.user.business_plus)
  },
  isBusinessPlusByCode (state) {
    return _.includes(BUSINESS_PLUS_PLANS, state.user.user_subscription?.plan?.code)
  },
  isAnnualPlan (state) {
    return _.includes(ANNUAL_PLANS, state.user.user_subscription?.plan?.code)
  }
}

const actions = {
  async signIn ({dispatch}, credentials) {
    if (process.env.NODE_ENV === 'development') {
      await axios.post('api/auth/login', credentials)
        .then(({data}) => {
          localStorage.setItem('sanctum_token', data.access_token)
          const loginFrame = document.getElementById('cross-iframe').contentWindow
          loginFrame.postMessage(JSON.stringify({key: 'sanctum_token', method: 'set', data: data.access_token}), '*')
          console.log('process.env.VUE_APP_MAIN_COOKIE_DOMAIN :>> ', process.env.VUE_APP_MAIN_COOKIE_DOMAIN)
          Cookie.set('cross_estage_token', data.access_token)
          return dispatch('me')
        })
    }
  },
  
  async signInTest ({dispatch}, credentials) {
    await axios.post('api/auth/new-login', credentials, {
      withCredentials: true,
      headers: {}
    })
      .then(() => {
        return dispatch('meTest')
      })
  },
  
  async meTest () {
    try {
      const { data } = await axios.get('api/auth/user-test', {
        withCredentials: true,
        headers: {}
      })
      console.log('me', data)
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  },

  async forget (_, payload) {
    return await axios.post('api/auth/password/forget', payload)
  },

  async reset (_, payload) {
    return await axios.post('api/auth/password/reset', {
      ...payload,
      token: new URLSearchParams(window.location.search).get('token')
    })
  },

  async signUp ({dispatch}, credentials) {
    await axios.post('api/auth/signup', credentials)
      .then(async ({data}) => {
        localStorage.setItem('sanctum_token', data)
        const loginFrame = document.getElementById('cross-iframe').contentWindow
        loginFrame.postMessage(JSON.stringify({key: 'sanctum_token', method: 'set', data: data}), '*')

        return await dispatch('me')
      })
  },

  async signOut ({commit}) {
    commit('SET_AUTHENTICATED', false)
    commit('SET_USER', null)
    Cookie.remove('cross_estage_token')
    window.location.href = process.env.VUE_APP_AUTH_HOST + '/logout'

    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('sanctum_token', null)
    }

    // await axios.get('api/auth/logout')
    //   .then(() => {
    //     localStorage.removeItem('sanctum_token')
    //     const loginFrame = document.getElementById('cross-iframe').contentWindow
    //     loginFrame.postMessage(JSON.stringify({key: 'sanctum_token', method: 'remove'}), '*')

    //     commit('SET_AUTHENTICATED', false)
    //     commit('SET_USER', null)

    //     router.push('/login')
    //   }).finally(()=>{
    //     Cookie.remove('cross_estage_token',{ domain: process.env.VUE_APP_MAIN_COOKIE_DOMAIN })
    //   })
  },

  async me ({ commit, getters }) {
    // Helper functions for cleaner conditions
    const isDevelopment = process.env.NODE_ENV === 'development'
    const getToken = () => isDevelopment
      ? localStorage.getItem('sanctum_token')
      : Cookie.get('cross_estage_token')

    const redirectTo = (path) => {
      window.open(path, '_self')
    }

    const isCurrentPage = (page) => {
      const currentPage = window.location.pathname.replace(/\/$/, '')
      return page instanceof RegExp ? page.test(currentPage) : currentPage === page
    }

    if (!getToken()) return false

    try {
      const { data } = await axios.get('api/auth/user')
      commit('SET_AUTHENTICATED', true)
      commit('SET_USER', data)

      // const shouldRedirectToSignup =
      //   !data.user_subscription && !(isCurrentPage('/signup') || isCurrentPage('/account'))

      // if (shouldRedirectToSignup) {
      //   redirectTo('/signup')
      // }

      const shouldRedirectToReactivate =
        data.user_subscription
        && (
          !_.includes(['succeeded', 'succeeeded'], data.user_subscription.status)
          || data.status_code === 'refunded'
          || data.user_subscription.plan.id === 7
          || data?.exp_date?.days_overdue >= 7
        )
        && !getters.isLifetime
        && !(isCurrentPage('/reactivate') || isCurrentPage('/account'))

      // const shouldRedirectToReactivate = data.status_code === 'refunded' || data?.user_subscription?.plan?.id === 7 && !(isCurrentPage('/reactivate') || isCurrentPage('/account'))

      if (shouldRedirectToReactivate) {
        redirectTo('/reactivate')
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      localStorage.removeItem('sanctum_token')
      const loginFrame = document.getElementById('cross-iframe')?.contentWindow
      if (loginFrame) {
        loginFrame.postMessage(JSON.stringify({ key: 'sanctum_token', method: 'remove' }), '*')
      }
      commit('SET_AUTHENTICATED', false)
      commit('SET_USER', null)
    }
  }
}

const mutations = {
  SET_AUTHENTICATED (state, paylaod) {
    state.authenticated = paylaod
  },

  SET_USER (state, payload) {
    state.user = payload
  },

  UPDATE_EMAIL (state, payload) {
    state.user.email = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
